/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import cstmTable from '../../../../share/components/vue/PaginationTable';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  SUCCESS: 'success',
  FAILED: 'failed'
};

export default {
  name: 'requirement095',
  data() {
    return {
      phase: PHASE.START,
      systemId: '',
      isLoading: false,
      isHidden: true,
      requestId: undefined,
      items: [],
      modal_requirement_095: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  components: {
    'cstm-table': cstmTable,
  },

  computed: {
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        default:
          return 'secondary';
      }
    },

    ...mapState({
      language: state => state.systems.language,
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    tableColumns() {
      return [
        { name: 'mapColumn', label: '' },
        { name: 'version', label: this.i18nWsKey + '.label_map_ver' },
        { name: 'released_at', label: this.i18nWsKey + '.label_release_date' },
      ];
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_095 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_095 = false;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      this.isLoading = false;
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
      return;
    },

    /**
    * API 通信で共通バリデーションでエラーが発生した際のコールバック
    * @param {object} result handler.validate で返ってきたバリデーション結果
    */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        // result を使って、任意の処理をすることも可能
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * Exit処理
     * @param {*} existence 0:エラー遷移あり, 1:エラー遷移なし
     * @returns
     */
    async exit(existence) {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (existence === 0) {
          // エラー遷移あり
          if (response) {
            // 正常コールバック
            switch (response.data.status) {
              case config.STATUS_WSP_COMPLETED:
                // 正常終了：機能終了
                this.closeWizard();
                break;
              case config.STATUS_WSP_FAILED:
              default:
                // 異常終了：汎用エラー
                this.showUnexpectedError();
                return;
            }
          }
        } else {
          // エラー遷移なし：機能終了
          this.closeWizard();
          return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** リリース日読出し */
    async readReleasedDate() {
      try {
        this.isLoading = true;
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'start'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：地図Ver読出しへ遷移
              this.requestId = response.data.user_data.request_id;
              await this.readMapVersion();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // 異常終了：共通エラー
              this.showUnexpectedError();
              return;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 地図Ver読出し */
    async readMapVersion() {
      try {
        this.isLoading = true;
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read',
            request_id: this.requestId
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：一覧データ取得処理
              // データ表示用変換
              this.phase = PHASE.SUCCESS;
              this.convertToTableItems(response.data.user_data.map_version);
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // 異常終了：共通エラー
              this.showUnexpectedError();
              return;
          }
        }
      } finally {
        this.isLoading = false;
        this.isHidden = false;
      }
    },

    /**
    * データ表示用変換
    * @param {object} response レスポンスデータ
    */
    convertToTableItems(response) {
      const areas = ['northern_europe', 'western_europe', 'eastern_europe', 'southern_europe', 'other_countries'];

      // 各地域ごとのデータを変換し、テーブルに追加
      areas.forEach((area) => {
        this.items.push({
          mapColumn: this.$t(`${this.i18nWsKey}.label_${area}`),
          version: response[area].version,
          released_at: response[area].released_at,
        });
      });
    }
  },

  async mounted() {
    this.init();
    // リリース日読出し
    this.readReleasedDate();
  }
};